/**
 * vibecheck firewall — Real-time AI code guardrails with truthpack-based drift detection
 *
 * Enhanced capabilities:
 *   analyze   — Deep analysis using 17-engine scanner + truthpack cross-reference
 *   watch     — Real-time file-save monitoring with instant verdicts
 *   drift     — Detect truthpack drift: routes, env vars, contracts that changed since last scan
 *   hook      — Install/uninstall git pre-commit hook that blocks bad code
 *   intent    — Lock an intent (what you're building) so the firewall can flag off-track changes
 *   rules     — List all active firewall rules (built-in + custom)
 *   report    — Activity summary with block/flag/pass stats over time
 *   status    — Current firewall mode and configuration
 */

import * as fs from 'fs';
import * as path from 'path';
import type { Command } from 'commander';
import ora from 'ora';
import type { GlobalOptions, ScanFinding } from '../types';
import { scanFile, resetScanner } from '../core/scanner';
import { walkFiles, walkChangedFiles } from '../core/file-walker';
import { ensureVibecheckDir } from '../core/config';
import * as out from '../ui/output';
import { colors, icons } from '../ui/theme';

// ── Try to load real 17-engine scanner ──────────────────────────────────────

let realScanner: any = null;
async function getScanner() {
  if (realScanner === null) {
    try { realScanner = await import('../../../engines'); } catch { realScanner = false; }
  }
  return realScanner || null;
}

// ── Types ───────────────────────────────────────────────────────────────────

interface FirewallOpts extends GlobalOptions {
  target?: string;
  strict?: boolean;
  whitelist?: string;
  mode?: 'observe' | 'enforce';
  engines?: string;
}

type FirewallVerdict = 'BLOCK' | 'FLAG' | 'PASS';

interface FileVerdict {
  file: string;
  verdict: FirewallVerdict;
  findings: ScanFinding[];
  driftIssues: DriftIssue[];
  intentViolations: string[];
}

interface DriftIssue {
  type: 'route-added' | 'route-removed' | 'env-added' | 'env-removed' | 'contract-changed';
  detail: string;
  file: string;
  line?: number;
}

interface TruthpackData {
  routes?: { path: string; method: string; file: string; line?: number }[];
  envVars?: { name: string; file: string }[];
  contracts?: { name: string; type: string; file: string }[];
  meta?: { generatedAt?: string; hash?: string };
}

interface IntentLock {
  summary: string;
  constraints: string[];
  allowedFiles: string[];
  lockedAt: string;
}

interface FirewallLog {
  entries: FirewallLogEntry[];
  lastUpdated: string;
}

interface FirewallLogEntry {
  timestamp: string;
  file: string;
  verdict: FirewallVerdict;
  findings: number;
  driftIssues: number;
  trigger: 'analyze' | 'watch' | 'hook' | 'manual';
}

// ── Registration ────────────────────────────────────────────────────────────

export function registerFirewall(program: Command): void {
  const cmd = program
    .command('firewall')
    .description('Real-time AI code guardrails with truthpack drift detection')
    .option('--strict', 'Block on any warning (not just critical/high)')
    .option('--whitelist <patterns>', 'Comma-separated file patterns to skip')
    .option('--mode <mode>', 'observe (log only) or enforce (block)', 'enforce')
    .option('--engines <list>', 'Comma-separated scanner engines to use');

  cmd.command('analyze [path]').description('Deep analysis: 17-engine scan + truthpack drift + intent check').action(async (filePath: string | undefined, _o: any, c: Command) => {
    await runAnalyze({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), target: filePath });
  });

  cmd.command('watch').description('Watch mode: real-time file-save monitoring with instant verdicts').action(async (_o: any, c: Command) => {
    await runWatch({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any) });
  });

  cmd.command('drift').description('Detect truthpack drift: routes, env vars, contracts that changed').action(async (_o: any, c: Command) => {
    await runDrift(c.parent!.parent!.opts() as GlobalOptions);
  });

  cmd.command('hook').description('Install git pre-commit hook that blocks bad code').action(async (_o: any, c: Command) => {
    await runHook(c.parent!.parent!.opts() as GlobalOptions, 'install');
  });

  cmd.command('unhook').description('Uninstall git pre-commit hook').action(async (_o: any, c: Command) => {
    await runHook(c.parent!.parent!.opts() as GlobalOptions, 'uninstall');
  });

  cmd.command('intent <summary>').description('Lock an intent — firewall flags off-track changes').action(async (summary: string, _o: any, c: Command) => {
    await runIntent({ ...(c.parent!.parent!.opts() as GlobalOptions) }, summary);
  });

  cmd.command('clear-intent').description('Clear the current intent lock').action(async (_o: any, c: Command) => {
    await runClearIntent(c.parent!.parent!.opts() as GlobalOptions);
  });

  cmd.command('rules').description('List all active firewall rules').action(async (_o: any, c: Command) => {
    await runRules(c.parent!.parent!.opts() as GlobalOptions);
  });

  cmd.command('add-rule <rule>').description('Add a custom firewall rule').action(async (rule: string, _o: any, c: Command) => {
    await runAddRule(c.parent!.parent!.opts() as GlobalOptions, rule);
  });

  cmd.command('report').description('Activity summary with block/flag/pass stats').action(async (_o: any, c: Command) => {
    await runReport(c.parent!.parent!.opts() as GlobalOptions);
  });

  cmd.command('status').description('Current firewall mode and configuration').action(async (_o: any, c: Command) => {
    await runStatus(c.parent!.parent!.opts() as GlobalOptions);
  });

  cmd.action(async (opts: any, command: Command) => {
    await runAnalyze({ ...(command.parent!.opts() as GlobalOptions), ...opts });
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// ANALYZE — Deep analysis with 17-engine scanner + truthpack + intent
// ═══════════════════════════════════════════════════════════════════════════════

async function runAnalyze(opts: FirewallOpts): Promise<void> {
  const start = performance.now();
  const rootPath = opts.path || process.cwd();
  resetScanner();

  out.brandHeader('Agent Firewall', 'Deep Analysis');
  out.nl();

  // Load truthpack and intent
  const truthpack = loadTruthpack(rootPath);
  const intent = loadIntent(rootPath);

  if (truthpack) {
    const routeCount = truthpack.routes?.length || 0;
    const envCount = truthpack.envVars?.length || 0;
    console.log(`  ${colors.dim('Truthpack:')} ${routeCount} routes, ${envCount} env vars`);
  }
  if (intent) {
    console.log(`  ${colors.dim('Intent:')} ${intent.summary}`);
  }
  out.nl();

  // Get files to analyze
  let files: string[];
  if (opts.target) {
    const abs = path.resolve(rootPath, opts.target);
    if (fs.existsSync(abs) && fs.statSync(abs).isDirectory()) {
      files = await walkFiles(rootPath, { include: [`${opts.target}/**/*.{ts,tsx,js,jsx}`] });
    } else {
      files = fs.existsSync(abs) ? [opts.target] : [];
    }
  } else {
    files = await walkChangedFiles(rootPath);
    if (files.length === 0) files = await walkFiles(rootPath, { maxFiles: 200 });
  }

  if (files.length === 0) { out.warn('No files found to analyze.'); return; }

  const spinner = ora(`Analyzing ${files.length} file${files.length > 1 ? 's' : ''} (17 engines + drift)...`).start();

  // Try real scanner first
  const scanner = await getScanner();
  const verdicts: FileVerdict[] = [];

  if (scanner) {
    spinner.text = 'Running 17-engine scanner...';
    try {
      const engineList = opts.engines ? opts.engines.split(',').map(e => e.trim()) : undefined;
      const report = await scanner.scan({
        projectRoot: rootPath,
        files,
        engines: engineList,
        severityThreshold: 'low',
        parallel: true,
      });
      const findings: ScanFinding[] = (report.findings || []).map((f: any) => ({
        ruleId: f.ruleId || f.rule || '',
        severity: f.severity || 'info',
        message: f.message || '',
        file: f.file || '',
        line: f.line,
        category: f.category || '',
        fix: f.fix,
        engine: f.engine,
      }));

      // Group findings by file
      const byFile = new Map<string, ScanFinding[]>();
      for (const f of findings) {
        const key = f.file || 'unknown';
        if (!byFile.has(key)) byFile.set(key, []);
        byFile.get(key)!.push(f);
      }

      for (const file of files) {
        const fileFindings = byFile.get(file) || [];
        const driftIssues = truthpack ? detectFileDrift(rootPath, file, truthpack) : [];
        const intentViolations = intent ? checkIntent(file, intent) : [];
        const verdict = computeVerdict(fileFindings, driftIssues, intentViolations, !!opts.strict);
        verdicts.push({ file, verdict, findings: fileFindings, driftIssues, intentViolations });
      }
    } catch {
      // Fall back to lite scanner
      spinner.text = 'Falling back to lite scanner...';
      for (const file of files) {
        const findings = scanFile(rootPath, file);
        const relevant = findings.filter(f => f.category === 'hallucination' || f.category === 'security');
        const driftIssues = truthpack ? detectFileDrift(rootPath, file, truthpack) : [];
        const intentViolations = intent ? checkIntent(file, intent) : [];
        const verdict = computeVerdict(relevant, driftIssues, intentViolations, !!opts.strict);
        verdicts.push({ file, verdict, findings: relevant, driftIssues, intentViolations });
      }
    }
  } else {
    for (const file of files) {
      const findings = scanFile(rootPath, file);
      const relevant = findings.filter(f => f.category === 'hallucination' || f.category === 'security');
      const driftIssues = truthpack ? detectFileDrift(rootPath, file, truthpack) : [];
      const intentViolations = intent ? checkIntent(file, intent) : [];
      const verdict = computeVerdict(relevant, driftIssues, intentViolations, !!opts.strict);
      verdicts.push({ file, verdict, findings: relevant, driftIssues, intentViolations });
    }
  }

  spinner.stop();
  const duration = performance.now() - start;

  // Log results
  logFirewallActivity(rootPath, verdicts, 'analyze');

  const blocked = verdicts.filter(v => v.verdict === 'BLOCK');
  const flagged = verdicts.filter(v => v.verdict === 'FLAG');
  const passed = verdicts.filter(v => v.verdict === 'PASS');

  if (opts.json) {
    out.jsonOutput({
      command: 'firewall',
      subcommand: 'analyze',
      verdicts: verdicts.map(v => ({ file: v.file, verdict: v.verdict, findings: v.findings.length, driftIssues: v.driftIssues.length, intentViolations: v.intentViolations.length })),
      stats: { blocked: blocked.length, flagged: flagged.length, passed: passed.length, total: verdicts.length },
      durationMs: Math.round(duration),
      mode: opts.mode || 'enforce',
    });
    process.exitCode = blocked.length > 0 ? 2 : flagged.length > 0 && opts.strict ? 1 : 0;
    return;
  }

  // Pretty output
  for (const v of verdicts) {
    if (v.verdict === 'PASS' && !opts.verbose) continue;

    const icon = v.verdict === 'BLOCK' ? colors.error('🚨 BLOCK')
      : v.verdict === 'FLAG' ? colors.warning('⚠️  FLAG')
      : colors.success('✅ PASS');

    console.log(`  ${icon}  ${colors.dim(v.file)}`);

    for (const f of v.findings.slice(0, 5)) {
      const sev = f.severity === 'critical' || f.severity === 'high' ? colors.error : colors.warning;
      console.log(`    ${sev(f.ruleId || '•')} ${f.message}`);
      if (f.fix) console.log(`    ${colors.info(icons.arrow)} ${colors.dim(f.fix)}`);
    }
    if (v.findings.length > 5) console.log(`    ${colors.dim(`... +${v.findings.length - 5} more`)}`);

    for (const d of v.driftIssues) {
      console.log(`    ${colors.warning('DRIFT')} ${d.detail}`);
    }

    for (const iv of v.intentViolations) {
      console.log(`    ${colors.warning('INTENT')} ${iv}`);
    }

    if (v.findings.length > 0 || v.driftIssues.length > 0) out.nl();
  }

  console.log(out.divider());
  console.log(`  ${colors.error(`${blocked.length} blocked`)} ${colors.dim('·')} ${colors.warning(`${flagged.length} flagged`)} ${colors.dim('·')} ${colors.success(`${passed.length} passed`)}`);
  console.log(out.completedIn(duration));
  out.nl();

  process.exitCode = blocked.length > 0 ? 2 : flagged.length > 0 && opts.strict ? 1 : 0;
}

// ═══════════════════════════════════════════════════════════════════════════════
// WATCH — Real-time file-save monitoring
// ═══════════════════════════════════════════════════════════════════════════════

async function runWatch(opts: FirewallOpts): Promise<void> {
  const rootPath = opts.path || process.cwd();
  const truthpack = loadTruthpack(rootPath);
  const intent = loadIntent(rootPath);

  out.brandHeader('Agent Firewall', 'Watch Mode');
  out.nl();
  console.log(`  ${colors.dim('Watching:')} ${colors.bold(rootPath)}`);
  if (truthpack) console.log(`  ${colors.dim('Truthpack:')} loaded (${truthpack.routes?.length || 0} routes)`);
  if (intent) console.log(`  ${colors.dim('Intent:')} ${intent.summary}`);
  console.log(colors.dim('  Press Ctrl+C to stop.'));
  out.nl();

  let sessionBlocked = 0;
  let sessionFlagged = 0;
  let sessionPassed = 0;

  try {
    const chokidar = require('chokidar');
    const watcher = chokidar.watch(['src/**/*.{ts,tsx,js,jsx}', 'app/**/*.{ts,tsx,js,jsx}', 'lib/**/*.{ts,tsx,js,jsx}', 'pages/**/*.{ts,tsx,js,jsx}'], {
      cwd: rootPath,
      ignoreInitial: true,
      ignored: ['**/node_modules/**', '**/dist/**', '**/.next/**'],
    });

    watcher.on('change', async (filePath: string) => {
      resetScanner();
      const findings = scanFile(rootPath, filePath);
      const relevant = findings.filter(f => f.category === 'hallucination' || f.category === 'security');
      const driftIssues = truthpack ? detectFileDrift(rootPath, filePath, truthpack) : [];
      const intentViolations = intent ? checkIntent(filePath, intent) : [];
      const verdict = computeVerdict(relevant, driftIssues, intentViolations, !!opts.strict);

      const time = new Date().toLocaleTimeString();

      if (verdict === 'BLOCK') {
        sessionBlocked++;
        console.log(`${colors.dim(time)} ${colors.error('🚨 BLOCK')} ${colors.dim(filePath)} — ${relevant.length} issue${relevant.length !== 1 ? 's' : ''}${driftIssues.length > 0 ? `, ${driftIssues.length} drift` : ''}`);
        for (const f of relevant.filter(f => f.severity === 'critical' || f.severity === 'high').slice(0, 3)) {
          console.log(`   ${colors.error(f.message)}`);
        }
      } else if (verdict === 'FLAG') {
        sessionFlagged++;
        console.log(`${colors.dim(time)} ${colors.warning('⚠️  FLAG')}  ${colors.dim(filePath)} — ${relevant.length + driftIssues.length} issue${relevant.length + driftIssues.length !== 1 ? 's' : ''}`);
      } else {
        sessionPassed++;
        console.log(`${colors.dim(time)} ${colors.success('✅ PASS')}  ${colors.dim(filePath)}`);
      }

      logFirewallActivity(rootPath, [{ file: filePath, verdict, findings: relevant, driftIssues, intentViolations }], 'watch');
    });

    // Periodic status line
    const statusInterval = setInterval(() => {
      const total = sessionBlocked + sessionFlagged + sessionPassed;
      if (total > 0 && total % 10 === 0) {
        console.log(colors.dim(`  ── Session: ${sessionBlocked} blocked · ${sessionFlagged} flagged · ${sessionPassed} passed ──`));
      }
    }, 30000);

    process.on('SIGINT', () => {
      clearInterval(statusInterval);
      out.nl();
      console.log(colors.dim(`  Session total: ${sessionBlocked} blocked · ${sessionFlagged} flagged · ${sessionPassed} passed`));
      process.exit(0);
    });

    await new Promise(() => {});
  } catch {
    out.error('chokidar is required for watch mode.', 'Install it with: npm install chokidar');
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// DRIFT — Detect truthpack drift
// ═══════════════════════════════════════════════════════════════════════════════

async function runDrift(opts: GlobalOptions): Promise<void> {
  const start = performance.now();
  const rootPath = opts.path || process.cwd();

  out.brandHeader('Truthpack Drift Detection');
  out.nl();

  const truthpack = loadTruthpack(rootPath);
  if (!truthpack) {
    out.warn('No truthpack found. Run a scan first to generate one:');
    console.log(`  ${colors.info('vibecheck scan')}`);
    return;
  }

  const spinner = ora('Scanning for drift...').start();
  const files = await walkFiles(rootPath, { maxFiles: 500 });
  const allDrift: DriftIssue[] = [];

  for (const file of files) {
    const drift = detectFileDrift(rootPath, file, truthpack);
    allDrift.push(...drift);
  }

  // Also check for removed routes/env vars
  const removedDrift = detectRemovedItems(rootPath, truthpack, files);
  allDrift.push(...removedDrift);

  spinner.stop();
  const duration = performance.now() - start;

  if (opts.json) {
    out.jsonOutput({ command: 'firewall', subcommand: 'drift', issues: allDrift, total: allDrift.length, durationMs: Math.round(duration) });
    process.exitCode = allDrift.length > 0 ? 1 : 0;
    return;
  }

  if (allDrift.length === 0) {
    out.success('No drift detected — code matches truthpack.');
    out.nl();
    console.log(out.completedIn(duration));
    return;
  }

  // Group by type
  const byType: Record<string, DriftIssue[]> = {};
  for (const d of allDrift) {
    if (!byType[d.type]) byType[d.type] = [];
    byType[d.type].push(d);
  }

  for (const [type, issues] of Object.entries(byType)) {
    const icon = type.includes('route') ? '🛤️' : type.includes('env') ? '🔑' : '📋';
    console.log(`  ${icon} ${colors.bold(type)} (${issues.length})`);
    for (const issue of issues.slice(0, 10)) {
      console.log(`    ${colors.warning('•')} ${issue.detail} ${colors.dim(`(${issue.file})`)}`);
    }
    if (issues.length > 10) console.log(`    ${colors.dim(`... +${issues.length - 10} more`)}`);
    out.nl();
  }

  console.log(out.divider());
  console.log(`  ${allDrift.length} drift issue${allDrift.length !== 1 ? 's' : ''} detected. Run ${colors.info('vibecheck scan')} to update truthpack.`);
  console.log(out.completedIn(duration));
  out.nl();
  process.exitCode = 1;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK — Git pre-commit hook
// ═══════════════════════════════════════════════════════════════════════════════

async function runHook(opts: GlobalOptions, action: 'install' | 'uninstall'): Promise<void> {
  const rootPath = opts.path || process.cwd();
  const hooksDir = path.join(rootPath, '.git', 'hooks');
  const hookPath = path.join(hooksDir, 'pre-commit');

  if (action === 'uninstall') {
    if (fs.existsSync(hookPath)) {
      const content = fs.readFileSync(hookPath, 'utf-8');
      if (content.includes('vibecheck firewall')) {
        fs.unlinkSync(hookPath);
        out.success('Pre-commit hook removed.');
      } else {
        out.warn('Pre-commit hook exists but was not installed by VibeCheck.');
      }
    } else {
      out.info('No pre-commit hook found.');
    }
    return;
  }

  if (!fs.existsSync(path.join(rootPath, '.git'))) {
    out.error('Not a git repository.', 'Initialize git first: git init');
    return;
  }

  if (!fs.existsSync(hooksDir)) fs.mkdirSync(hooksDir, { recursive: true });

  const hookContent = `#!/bin/sh
# VibeCheck Agent Firewall — Pre-commit hook
# Blocks commits with critical security or hallucination issues
# Installed by: vibecheck firewall hook

echo "🛡️  VibeCheck Firewall: scanning staged files..."

# Run firewall on staged files
npx vibecheck-ai firewall analyze --json --strict 2>/dev/null | node -e "
  let data = '';
  process.stdin.on('data', d => data += d);
  process.stdin.on('end', () => {
    try {
      const result = JSON.parse(data);
      const blocked = result.stats?.blocked || 0;
      if (blocked > 0) {
        console.error('\\n🚨 VibeCheck Firewall BLOCKED this commit:');
        console.error('   ' + blocked + ' file(s) have critical issues.');
        console.error('   Run: vibecheck firewall analyze');
        process.exit(1);
      } else {
        console.log('✅ VibeCheck Firewall: all clear');
      }
    } catch {
      // If parsing fails, allow commit
      console.log('⚠️  VibeCheck Firewall: could not parse results, allowing commit');
    }
  });
"
`;

  fs.writeFileSync(hookPath, hookContent, { mode: 0o755 });
  out.success('Pre-commit hook installed!');
  console.log(`  ${colors.dim('Location:')} ${hookPath}`);
  console.log(`  ${colors.dim('Effect:')} Blocks commits with critical security/hallucination issues`);
  console.log(`  ${colors.dim('Remove:')} ${colors.info('vibecheck firewall unhook')}`);
  out.nl();
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTENT — Lock what you're building
// ═══════════════════════════════════════════════════════════════════════════════

async function runIntent(opts: GlobalOptions, summary: string): Promise<void> {
  const rootPath = opts.path || process.cwd();
  const intentPath = path.join(ensureVibecheckDir(rootPath), 'intent.json');

  // Parse constraints from summary (e.g., "Add auth --only src/auth/ src/middleware/")
  const parts = summary.split('--only');
  const intentSummary = parts[0].trim();
  const allowedFiles = parts[1] ? parts[1].trim().split(/\s+/) : [];

  const intent: IntentLock = {
    summary: intentSummary,
    constraints: [],
    allowedFiles,
    lockedAt: new Date().toISOString(),
  };

  fs.writeFileSync(intentPath, JSON.stringify(intent, null, 2), 'utf-8');

  out.success(`Intent locked: "${intentSummary}"`);
  if (allowedFiles.length > 0) {
    console.log(`  ${colors.dim('Allowed files:')} ${allowedFiles.join(', ')}`);
  }
  console.log(`  ${colors.dim('The firewall will flag changes that seem off-track.')}`);
  console.log(`  ${colors.dim('Clear with:')} ${colors.info('vibecheck firewall clear-intent')}`);
  out.nl();
}

async function runClearIntent(opts: GlobalOptions): Promise<void> {
  const rootPath = opts.path || process.cwd();
  const intentPath = path.join(rootPath, '.vibecheck', 'intent.json');
  if (fs.existsSync(intentPath)) {
    fs.unlinkSync(intentPath);
    out.success('Intent lock cleared.');
  } else {
    out.info('No intent lock found.');
  }
}

// RULES — List active rules
// ═══════════════════════════════════════════════════════════════════════════════

const BUILTIN_RULES: { id: string; name: string; severity: string; category: string; description: string }[] = [
  // Hallucination rules
  { id: 'HAL001', name: 'Hallucinated Package Import', severity: 'high', category: 'hallucination', description: 'Blocks imports of known non-existent npm packages' },
  { id: 'HAL002', name: 'Ghost Import', severity: 'medium', category: 'hallucination', description: 'Flags relative imports that don\'t resolve to existing files' },
  { id: 'HAL003', name: 'Suspicious Import Pattern', severity: 'low', category: 'hallucination', description: 'Flags imports matching common AI hallucination patterns' },
  { id: 'HAL004', name: 'Fabricated CSS Property', severity: 'low', category: 'hallucination', description: 'Flags CSS properties that don\'t exist in any browser' },
  { id: 'HAL005', name: 'Unknown Env Var', severity: 'medium', category: 'hallucination', description: 'Flags env vars not in .env or .env.example' },
  { id: 'HAL006', name: 'Fabricated API Endpoint', severity: 'medium', category: 'hallucination', description: 'Flags API endpoints that look AI-generated' },
  { id: 'HAL007', name: 'AI Skeleton Stub', severity: 'medium', category: 'hallucination', description: 'Flags TODO stubs with placeholder return values' },
  // Security rules (OWASP Top 10)
  { id: 'SEC001', name: 'Hardcoded Secret', severity: 'critical', category: 'security', description: 'Blocks hardcoded API keys, tokens, passwords, connection strings' },
  { id: 'SEC002', name: 'SQL Injection', severity: 'critical', category: 'security', description: 'Blocks user input interpolated into SQL queries' },
  { id: 'SEC003', name: 'XSS Vulnerability', severity: 'high', category: 'security', description: 'Blocks unsanitized HTML injection (innerHTML, dangerouslySetInnerHTML)' },
  { id: 'SEC004', name: 'Insecure Randomness', severity: 'high', category: 'security', description: 'Blocks Math.random() for security-sensitive values' },
  { id: 'SEC005', name: 'Path Traversal', severity: 'high', category: 'security', description: 'Blocks user input in file system operations' },
  { id: 'SEC006', name: 'Code Injection', severity: 'critical', category: 'security', description: 'Blocks eval() and new Function() usage' },
  { id: 'SEC007', name: 'SSRF', severity: 'high', category: 'security', description: 'Blocks user input in outbound HTTP request URLs' },
  { id: 'SEC008', name: 'Prototype Pollution', severity: 'high', category: 'security', description: 'Blocks unsafe object merges with user input' },
  { id: 'SEC009', name: 'Open Redirect', severity: 'medium', category: 'security', description: 'Blocks user-controlled redirect destinations' },
  { id: 'SEC010', name: 'Permissive CORS', severity: 'medium', category: 'security', description: 'Flags wildcard or missing CORS configuration' },
  { id: 'SEC011', name: 'ReDoS', severity: 'medium', category: 'security', description: 'Blocks user input used to construct regex patterns' },
  { id: 'SEC012', name: 'Insecure Deserialization', severity: 'medium', category: 'security', description: 'Flags parsing user input without schema validation' },
  { id: 'SEC013', name: 'Timing Attack', severity: 'medium', category: 'security', description: 'Flags string comparison on secrets/tokens' },
  { id: 'SEC014', name: 'Insecure Cookie', severity: 'medium', category: 'security', description: 'Flags auth cookies missing httpOnly/secure/sameSite' },
  { id: 'SEC015', name: 'Disabled Security', severity: 'high', category: 'security', description: 'Blocks explicitly disabled security middleware' },
  { id: 'SEC016', name: 'Hardcoded Localhost', severity: 'low', category: 'security', description: 'Flags hardcoded localhost URLs in production code' },
  { id: 'SEC017', name: 'Command Injection', severity: 'critical', category: 'security', description: 'Blocks user input passed to shell execution' },
  { id: 'SEC018', name: 'Missing Rate Limiting', severity: 'medium', category: 'security', description: 'Flags auth endpoints without rate limiting' },
  { id: 'SEC019', name: 'Unvalidated Upload', severity: 'medium', category: 'security', description: 'Flags file uploads without type/size validation' },
  { id: 'SEC020', name: 'Exposed Error Details', severity: 'low', category: 'security', description: 'Flags stack traces sent to clients' },
];

function loadCustomRules(rootPath: string): string[] {
  const rulesFile = path.join(rootPath, '.vibecheck', 'firewall-rules', 'custom-rules.json');
  if (fs.existsSync(rulesFile)) {
    try { return JSON.parse(fs.readFileSync(rulesFile, 'utf-8')); } catch { /* ignore */ }
  }
  return [];
}

async function runAddRule(opts: GlobalOptions, rule: string): Promise<void> {
  const rootPath = opts.path || process.cwd();
  const rulesDir = path.join(ensureVibecheckDir(rootPath), 'firewall-rules');
  if (!fs.existsSync(rulesDir)) fs.mkdirSync(rulesDir, { recursive: true });

  const rulesFile = path.join(rulesDir, 'custom-rules.json');
  const existing = loadCustomRules(rootPath);
  existing.push(rule);
  fs.writeFileSync(rulesFile, JSON.stringify(existing, null, 2), 'utf-8');

  out.success(`Added custom rule: "${rule}"`);
  console.log(`Saved to: ${colors.dim(rulesFile)}`);
}

async function runRules(opts: GlobalOptions): Promise<void> {
  const rootPath = opts.path || process.cwd();
  const customRules = loadCustomRules(rootPath);

  out.brandHeader('Firewall Rules', `${BUILTIN_RULES.length} built-in + ${customRules.length} custom`);
  out.nl();

  console.log(out.table(
    ['Rule', 'Severity', 'Category', 'Description'],
    BUILTIN_RULES.map(r => [
      colors.info(r.id),
      r.severity === 'critical' ? colors.critical(r.severity) : r.severity === 'high' ? colors.high(r.severity) : r.severity === 'medium' ? colors.medium(r.severity) : colors.dim(r.severity),
      colors.dim(r.category),
      r.description,
    ])
  ));

  if (customRules.length > 0) {
    out.nl();
    console.log(colors.bold('  Custom Rules:'));
    for (const rule of customRules) {
      console.log(`    ${colors.info('•')} ${rule}`);
    }
  }

  out.nl();
  console.log(`  ${BUILTIN_RULES.length} built-in rules active. Add custom rules: ${colors.info('vibecheck firewall add-rule "description"')}`);
  out.nl();
}

// ═══════════════════════════════════════════════════════════════════════════════
// REPORT — Activity summary
// ═══════════════════════════════════════════════════════════════════════════════

async function runReport(opts: GlobalOptions): Promise<void> {
  const rootPath = opts.path || process.cwd();

  out.brandHeader('Firewall Report', 'Activity summary');
  out.nl();

  // Check for recent scan results
  const scanResultPath = path.join(rootPath, '.vibecheck', 'scan-results.json');
  if (fs.existsSync(scanResultPath)) {
    try {
      const data = JSON.parse(fs.readFileSync(scanResultPath, 'utf-8'));
      const findings = data.findings || [];
      const halFindings = findings.filter((f: any) => f.category === 'hallucination');
      const secFindings = findings.filter((f: any) => f.category === 'security');
      const qualFindings = findings.filter((f: any) => f.category === 'quality');
      const criticals = findings.filter((f: any) => f.severity === 'critical');
      const highs = findings.filter((f: any) => f.severity === 'high');

      console.log(`  ${icons.shield} Hallucinations blocked: ${colors.bold(String(halFindings.length))}`);
      console.log(`  ${icons.lock} Security issues caught:  ${colors.bold(String(secFindings.length))}`);
      console.log(`  ${icons.gear} Quality issues found:   ${colors.bold(String(qualFindings.length))}`);
      console.log(`  ${icons.chart} Total findings:         ${colors.bold(String(findings.length))}`);
      out.nl();

      // Severity breakdown
      console.log(`  ${colors.bold('Severity Breakdown:')}`);
      if (criticals.length > 0) console.log(`    ${colors.critical(`${criticals.length} critical`)} — must fix before deploy`);
      if (highs.length > 0) console.log(`    ${colors.high(`${highs.length} high`)} — should fix soon`);
      const mediums = findings.filter((f: any) => f.severity === 'medium').length;
      const lows = findings.filter((f: any) => f.severity === 'low').length;
      if (mediums > 0) console.log(`    ${colors.medium(`${mediums} medium`)}`);
      if (lows > 0) console.log(`    ${colors.low(`${lows} low`)}`);
      out.nl();

      // Top rules triggered
      const ruleCount: Record<string, number> = {};
      for (const f of findings) {
        ruleCount[f.ruleId] = (ruleCount[f.ruleId] || 0) + 1;
      }
      const topRules = Object.entries(ruleCount).sort((a, b) => b[1] - a[1]).slice(0, 5);
      if (topRules.length > 0) {
        console.log(`  ${colors.bold('Top Rules Triggered:')}`);
        for (const [rule, count] of topRules) {
          console.log(`    ${colors.dim(rule)}: ${colors.bold(String(count))} occurrences`);
        }
      }

      // Scan timestamp
      if (data.timestamp) {
        out.nl();
        console.log(`  ${colors.dim(`Last scan: ${data.timestamp}`)}`);
      }
    } catch { /* ignore */ }
  } else {
    out.info('No scan data found. Run a scan first:');
    console.log(`  ${colors.info('vibecheck scan')}`);
  }
  out.nl();
}

// ═══════════════════════════════════════════════════════════════════════════════
// STATUS — Current configuration
// ═══════════════════════════════════════════════════════════════════════════════

async function runStatus(opts: GlobalOptions): Promise<void> {
  const rootPath = opts.path || process.cwd();

  out.brandHeader('Firewall Status');
  out.nl();

  // Truthpack
  const truthpack = loadTruthpack(rootPath);
  if (truthpack) {
    console.log(`  ${colors.success('✓')} Truthpack loaded`);
    console.log(`    Routes: ${truthpack.routes?.length || 0} · Env vars: ${truthpack.envVars?.length || 0} · Contracts: ${truthpack.contracts?.length || 0}`);
    if (truthpack.meta?.generatedAt) console.log(`    Generated: ${colors.dim(truthpack.meta.generatedAt)}`);
  } else {
    console.log(`  ${colors.warning('○')} No truthpack found`);
  }

  // Intent
  const intent = loadIntent(rootPath);
  if (intent) {
    console.log(`  ${colors.success('✓')} Intent locked: ${intent.summary}`);
    if (intent.allowedFiles.length > 0) console.log(`    Allowed files: ${intent.allowedFiles.join(', ')}`);
  } else {
    console.log(`  ${colors.dim('○')} No intent lock`);
  }

  // Pre-commit hook
  const hookPath = path.join(rootPath, '.git', 'hooks', 'pre-commit');
  if (fs.existsSync(hookPath) && fs.readFileSync(hookPath, 'utf-8').includes('vibecheck')) {
    console.log(`  ${colors.success('✓')} Pre-commit hook installed`);
  } else {
    console.log(`  ${colors.dim('○')} No pre-commit hook`);
  }

  // Custom rules
  const customRulesFile = path.join(rootPath, '.vibecheck', 'firewall-rules', 'custom-rules.json');
  if (fs.existsSync(customRulesFile)) {
    try {
      const rules = JSON.parse(fs.readFileSync(customRulesFile, 'utf-8'));
      console.log(`  ${colors.success('✓')} ${rules.length} custom rule${rules.length !== 1 ? 's' : ''}`);
    } catch { /* ignore */ }
  }

  // Activity log
  const log = loadFirewallLog(rootPath);
  if (log.entries.length > 0) {
    console.log(`  ${colors.success('✓')} ${log.entries.length} activity log entries`);
  }

  out.nl();
}

// ═══════════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function loadTruthpack(rootPath: string): TruthpackData | null {
  const paths = [
    path.join(rootPath, '.vibecheck', 'truthpack.json'),
    path.join(rootPath, '.vibecheck', 'scan-results.json'),
  ];
  for (const p of paths) {
    if (fs.existsSync(p)) {
      try {
        const data = JSON.parse(fs.readFileSync(p, 'utf-8'));
        if (data.routes || data.envVars || data.contracts) return data;
        if (data.truthpack) return data.truthpack;
      } catch { /* ignore */ }
    }
  }
  return null;
}

function loadIntent(rootPath: string): IntentLock | null {
  const intentPath = path.join(rootPath, '.vibecheck', 'intent.json');
  if (fs.existsSync(intentPath)) {
    try { return JSON.parse(fs.readFileSync(intentPath, 'utf-8')); } catch { /* ignore */ }
  }
  return null;
}

function computeVerdict(findings: ScanFinding[], driftIssues: DriftIssue[], intentViolations: string[], strict: boolean): FirewallVerdict {
  const criticals = findings.filter(f => f.severity === 'critical').length;
  const highs = findings.filter(f => f.severity === 'high').length;
  const mediums = findings.filter(f => f.severity === 'medium').length;

  if (criticals > 0) return 'BLOCK';
  if (highs > 0) return 'BLOCK';
  if (strict && (mediums > 0 || driftIssues.length > 0 || intentViolations.length > 0)) return 'BLOCK';
  if (mediums > 0 || driftIssues.length > 0 || intentViolations.length > 0) return 'FLAG';
  return 'PASS';
}

function detectFileDrift(rootPath: string, file: string, truthpack: TruthpackData): DriftIssue[] {
  const issues: DriftIssue[] = [];
  let content: string;
  try {
    const absPath = path.resolve(rootPath, file);
    content = fs.readFileSync(absPath, 'utf-8');
  } catch { return issues; }

  // Check for new routes not in truthpack
  const routePatterns = [
    /(?:app|router)\.(get|post|put|patch|delete)\s*\(\s*['"`]([^'"`]+)['"`]/gi,
    /export\s+(?:async\s+)?function\s+(GET|POST|PUT|PATCH|DELETE)\s*\(/gi,
  ];

  const truthpackRoutes = new Set((truthpack.routes || []).map(r => `${r.method}:${r.path}`));

  for (const pattern of routePatterns) {
    const re = new RegExp(pattern.source, pattern.flags);
    let m: RegExpExecArray | null;
    while ((m = re.exec(content)) !== null) {
      const method = m[1].toUpperCase();
      const routePath = m[2] || `/${file.replace(/\.(ts|tsx|js|jsx)$/, '')}`;
      const key = `${method}:${routePath}`;
      if (!truthpackRoutes.has(key)) {
        const line = content.substring(0, m.index).split('\n').length;
        issues.push({ type: 'route-added', detail: `New route ${method} ${routePath} not in truthpack`, file, line });
      }
    }
  }

  // Check for new env vars not in truthpack
  const envPattern = /process\.env\.(\w+)/g;
  const truthpackEnvVars = new Set((truthpack.envVars || []).map(e => e.name));
  let envMatch: RegExpExecArray | null;
  const envRe = new RegExp(envPattern.source, envPattern.flags);
  while ((envMatch = envRe.exec(content)) !== null) {
    const name = envMatch[1];
    if (!truthpackEnvVars.has(name) && !['NODE_ENV', 'HOME', 'PATH', 'USERPROFILE', 'CI'].includes(name)) {
      const line = content.substring(0, envMatch.index).split('\n').length;
      issues.push({ type: 'env-added', detail: `New env var ${name} not in truthpack`, file, line });
    }
  }

  return issues;
}

function detectRemovedItems(rootPath: string, truthpack: TruthpackData, files: string[]): DriftIssue[] {
  const issues: DriftIssue[] = [];

  // Check if route handler files still exist
  for (const route of (truthpack.routes || [])) {
    if (route.file && !files.includes(route.file)) {
      const absPath = path.resolve(rootPath, route.file);
      if (!fs.existsSync(absPath)) {
        issues.push({ type: 'route-removed', detail: `Route file ${route.file} (${route.method} ${route.path}) no longer exists`, file: route.file });
      }
    }
  }

  return issues;
}

function checkIntent(file: string, intent: IntentLock): string[] {
  const violations: string[] = [];

  if (intent.allowedFiles.length > 0) {
    const isAllowed = intent.allowedFiles.some(pattern => {
      if (pattern.endsWith('/')) return file.startsWith(pattern);
      return file === pattern || file.startsWith(pattern);
    });
    if (!isAllowed) {
      violations.push(`File '${file}' is outside intent scope (${intent.summary})`);
    }
  }

  return violations;
}

// ── Firewall Activity Log ───────────────────────────────────────────────────

function loadFirewallLog(rootPath: string): FirewallLog {
  const logPath = path.join(rootPath, '.vibecheck', 'firewall-log.json');
  if (fs.existsSync(logPath)) {
    try { return JSON.parse(fs.readFileSync(logPath, 'utf-8')); } catch { /* ignore */ }
  }
  return { entries: [], lastUpdated: new Date().toISOString() };
}

function logFirewallActivity(rootPath: string, verdicts: FileVerdict[], trigger: FirewallLogEntry['trigger']): void {
  try {
    const vibecheckDir = path.join(rootPath, '.vibecheck');
    if (!fs.existsSync(vibecheckDir)) fs.mkdirSync(vibecheckDir, { recursive: true });

    const logPath = path.join(vibecheckDir, 'firewall-log.json');
    const log = loadFirewallLog(rootPath);

    for (const v of verdicts) {
      log.entries.push({
        timestamp: new Date().toISOString(),
        file: v.file,
        verdict: v.verdict,
        findings: v.findings.length,
        driftIssues: v.driftIssues.length,
        trigger,
      });
    }

    // Keep last 500 entries
    if (log.entries.length > 500) log.entries = log.entries.slice(-500);
    log.lastUpdated = new Date().toISOString();

    fs.writeFileSync(logPath, JSON.stringify(log, null, 2), 'utf-8');
  } catch { /* non-critical */ }
}
